<template>
  <b-container fluid class="project-list-wrapper">
    <b-row class="text-left">
      <b-col class="mb-3 mt-3">
        <h1>Project List</h1>
      </b-col>
    </b-row>
    <b-row>
      <b-col>
        <BaseMessageBlock :messages="errors"></BaseMessageBlock>
      </b-col>
    </b-row>
    <LoadingIndicator 
      v-if="loading"
      topic="Projects"
    />
    <div v-if="!loading">
      <b-row>
        <b-col>
          <div class="tool-bar">
            <div class="select-wrapper">
              <b-form-group 
                label="Status" 
                label-cols=auto 
                label-for="filterByStatus" 
                label-class="mb-0"
                class="d-flex align-items-center"
              >
                <b-form-select v-model="statusFilter" id="filterByStatus" class="w-auto">
                  <option value="All">All</option>
                  <option value="Open">Open</option>
                </b-form-select>
              </b-form-group>
            </div>
            <div class="custom-search" style="width:40%; max-width:450px; min-width: 250px;">
              <b-input-group>
                  <b-input-group-prepend is-text>
                    <b-icon icon="search"></b-icon>
                  </b-input-group-prepend>
                  <b-form-input v-model="searchFilter" type="search" placeholder="Search for a Project"></b-form-input>
              </b-input-group>
            </div>
            <b-button variant="tertiary"><b-icon icon="funnel"></b-icon></b-button>
            <b-button class="align-right" variant="primary" @click="newProject">Create Project</b-button>
          </div>
        </b-col>
      </b-row>
      <b-row class="text-left">
        <b-col>
          <b-table 
            hover 
            responsive
            class="project-list custom-table" 
            :items="filteredItems" 
            :fields="fields" 
            :filter="searchFilter"
            @row-clicked="openProject"
          >
            <template v-slot:cell(projectIdentifier)="data">
              {{data.item.projectIdentifier || "--"}}
            </template>
            <template v-slot:cell(primaryContractor)="data">
              <span v-if="data.item.primaryContractorLogoUrl">
                <img :src="data.item.primaryContractorLogoUrl" class="table-logo" :alt="data.item.primaryContractor" :title="data.item.primaryContractor">
              </span>
              <span v-else>
                {{data.item.primaryContractor}}
              </span>
            </template>
            <template v-slot:cell(primaryOwner)="data">
              <span v-if="data.item.primaryOwnerLogoUrl">
                <img :src="data.item.primaryOwnerLogoUrl" class="table-logo" :alt="data.item.primaryOwner" :title="data.item.primaryOwner">
              </span>
              <span v-else>
                {{data.item.primaryOwner}}
              </span>
            </template>
            <template v-slot:cell(totalDollarsSaved)="data">
              <span :class="{loss : data.item.totalDollarsSaved < 0, gain : data.item.totalDollarsSaved > 0}">
                {{$formatter.toCurrency(data.item.totalDollarsSaved) || "$0"}}
              </span>
            </template>
            <template v-slot:cell(totalTimeSaved)="data">
              <span :class="{loss : data.item.totalTimeSaved < 0, gain : data.item.totalTimeSaved > 0}">
                {{data.item.totalTimeSaved + " days"}}
              </span>
            </template>
            <template v-slot:cell(status)="data">
              <div class="lvl">
                <span>{{data.item.status}}</span>
              </div>
            </template>
            <template v-slot:cell(kebab)="data">
              <b-dropdown id="dropdown-1" class="kebab" text="Additional Options" variant="tertiary" no-caret offset="-125">
                <template #button-content>
                  <b-icon class="icon kebab additional" icon="three-dots-vertical" font-scale="1"></b-icon><span class="sr-only">Additional Options</span>
                </template>
                <b-dropdown-item :to="'/project/' + data.item.projectId">Project Details</b-dropdown-item>
                <b-dropdown-item v-if="isSystemAdmin" :to="'/project/' + data.item.projectId + '/edit'">Edit Project</b-dropdown-item>
                <b-dropdown-item v-if="isSystemAdmin" :to="'/project/' + data.item.projectId + '#users-tab'">View Users</b-dropdown-item>
              </b-dropdown>
            </template>
          </b-table>
        </b-col>
      </b-row>
    </div>
    <EditProjectModal
      ref="new-project-modal"
      :mode="'Add'"
      @submit="newProjectSubmit">
    </EditProjectModal>
  </b-container>
</template>
<script>
import axios from "axios";
import EditProjectModal from "@/modal-components/EditProjectModal.vue";

export default {
  name: "ProjectList",
  components: {
    EditProjectModal
  },
  data() {
    return {
      errors: [],
      projects: [],
      searchFilter: null,
      statusFilter: "All",
      loading: false,
      fields: [
        { key: "projectName", label: "Project Name"},
        { key: 'projectIdentifier', label: 'ID' },
        { key: 'primaryContractor', label: 'Contractor' },
        { key: 'primaryOwner', label: 'Owner' },
        { key: 'safetyInnovations', label: 'Safety Inn.' },
        { key: 'totalTimeSaved', label: 'Time Saved' },
        { key: 'totalDollarsSaved', label: '$ Saved' },
        { key: "status", label: "Status",
          tdClass: (value) => value.toLowerCase().replace(/ /g,'-')  },
          { key: 'kebab', label: '' }
      ]
    };
  },
  mounted() {
    if (!this.accountUser.admin && !this.accountUser.systemAdmin) {
      this.defaultRedirect();
    }
    this.loading = true;
    axios
      .get(`${this.$api_url}/projects-list`, this.getReqConfig())
      .then(response => {
        this.projects = response.data;
        let ordering = ["Open", "Closed"]
        this.projects
          .sort(function(a, b) { return a.projectName.toLowerCase() > b.projectName.toLowerCase() ? -1 : 1; })
          .sort((a, b) =>
            ordering.indexOf(a.status) > ordering.indexOf(b.status) ? 1 : -1);
      })
      .catch(err => {
        this.errors.push(err);
      })
      .finally(() => {
        this.loading = false;
      });
  },
  methods: {
    newProject() {
      this.$refs['new-project-modal'].show();
    },
    openProject(item){
      this.$router.push('/project/' + item.projectId);
    },
    newProjectSubmit(projectId) {
      this.$router.push('/project/' + projectId);
    },
  },
  computed: {
    filteredItems() {
      if (this.statusFilter === 'Open') {
        return this.openItems;
      } 
      else return this.projects;
    },
    openItems() {
      return this.projects.filter(p => p.status == 'Open');
    },
  }
};
</script>
<style scoped>
#filterByStatus{
  display: inline !important;
}
</style>